import { ERR, EvalValue, isErr } from './types';

export type Arg = EvalValue | EvalValue[][];

function flat(args: Arg[]): EvalValue[] {
  const out: EvalValue[] = [];
  for (const a of args) {
    if (Array.isArray(a)) for (const row of a) for (const v of row) out.push(v);
    else out.push(a);
  }
  return out;
}

function firstErr(vals: EvalValue[]): EvalValue | null {
  for (const v of vals) if (isErr(v)) return v;
  return null;
}

export function toNum(v: Arg): number | { err: string } {
  if (Array.isArray(v)) v = v[0]?.[0] ?? null;
  if (isErr(v)) return v;
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : ERR.VALUE;
}

export function toStr(v: Arg): string | { err: string } {
  if (Array.isArray(v)) v = v[0]?.[0] ?? null;
  if (isErr(v)) return v;
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v);
}

export function toBool(v: Arg): boolean | { err: string } {
  if (Array.isArray(v)) v = v[0]?.[0] ?? null;
  if (isErr(v)) return v;
  if (v == null || v === '') return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v).trim().toUpperCase();
  if (s === 'TRUE') return true;
  if (s === 'FALSE') return false;
  return ERR.VALUE;
}

function nums(args: Arg[], skipNonNumeric = true): number[] | { err: string } {
  const vals = flat(args);
  const e = firstErr(vals);
  if (e) return e as { err: string };
  const out: number[] = [];
  for (const v of vals) {
    if (v == null || v === '') continue;
    if (typeof v === 'number') { out.push(v); continue; }
    if (typeof v === 'boolean') { if (!skipNonNumeric) out.push(v ? 1 : 0); continue; }
    const n = Number(String(v).trim());
    if (Number.isFinite(n)) out.push(n);
    else if (!skipNonNumeric) return ERR.VALUE;
  }
  return out;
}

function agg(fn: (ns: number[]) => number, empty: number | { err: string } = 0) {
  return (args: Arg[]): Arg => {
    const ns = nums(args);
    if (!Array.isArray(ns)) return ns;
    if (!ns.length) return empty as any;
    const out = fn(ns);
    return Number.isNaN(out) ? ERR.NUM : out;
  };
}

const DAY_MS = 86400000;
const EPOCH = Date.UTC(1899, 11, 30);

export function dateToSerial(d: Date): number {
  return (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - EPOCH) / DAY_MS;
}

export function serialToDate(n: number): Date {
  return new Date(EPOCH + n * DAY_MS);
}

export function matchCriteria(v: EvalValue, crit: EvalValue): boolean {
  if (typeof crit === 'string') {
    const m = /^(<>|<=|>=|=|<|>)(.*)$/.exec(crit);
    if (m) {
      const op = m[1];
      const rhsRaw = m[2];
      const rhsNum = Number(rhsRaw);
      const isNum = rhsRaw.trim() !== '' && Number.isFinite(rhsNum);
      if (isNum && typeof v === 'number') {
        switch (op) {
          case '=': return v === rhsNum;
          case '<>': return v !== rhsNum;
          case '<': return v < rhsNum;
          case '>': return v > rhsNum;
          case '<=': return v <= rhsNum;
          case '>=': return v >= rhsNum;
        }
      }
      const ls = v == null ? '' : String(v).toLowerCase();
      const rs = rhsRaw.toLowerCase();
      switch (op) {
        case '=': return ls === rs;
        case '<>': return ls !== rs;
        case '<': return ls < rs;
        case '>': return ls > rs;
        case '<=': return ls <= rs;
        case '>=': return ls >= rs;
      }
    }
    if (/[*?]/.test(crit)) {
      const re = new RegExp('^' + crit.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.') + '$', 'i');
      return re.test(v == null ? '' : String(v));
    }
    const cn = Number(crit);
    if (crit.trim() !== '' && Number.isFinite(cn) && typeof v === 'number') return v === cn;
    return String(v ?? '').toLowerCase() === crit.toLowerCase();
  }
  if (typeof crit === 'number') return typeof v === 'number' ? v === crit : Number(v) === crit;
  if (typeof crit === 'boolean') return v === crit;
  if (crit == null) return v == null || v === '';
  return false;
}

function asGrid(a: Arg): EvalValue[][] {
  if (Array.isArray(a)) return a;
  return [[a]];
}

function condPairs(args: Arg[]): Array<[EvalValue[][], EvalValue]> | { err: string } {
  const pairs: Array<[EvalValue[][], EvalValue]> = [];
  for (let i = 0; i < args.length; i += 2) {
    const grid = asGrid(args[i]);
    let crit = args[i + 1];
    if (Array.isArray(crit)) crit = crit[0]?.[0] ?? null;
    if (isErr(crit)) return crit as { err: string };
    pairs.push([grid, crit as EvalValue]);
  }
  return pairs;
}

function multiCond(valueGrid: EvalValue[][] | null, pairs: Array<[EvalValue[][], EvalValue]>, fn: (vals: EvalValue[]) => Arg): Arg {
  const base = valueGrid || pairs[0][0];
  const rows = base.length;
  const cols = base[0]?.length || 0;
  const picked: EvalValue[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let ok = true;
      for (const [grid, crit] of pairs) {
        if (!matchCriteria(grid[r]?.[c] ?? null, crit)) { ok = false; break; }
      }
      if (ok) picked.push(valueGrid ? valueGrid[r]?.[c] ?? null : base[r]?.[c] ?? null);
    }
  }
  return fn(picked);
}

function cmpVals(a: EvalValue, b: EvalValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const as = String(a ?? '').toLowerCase();
  const bs = String(b ?? '').toLowerCase();
  return as < bs ? -1 : as > bs ? 1 : 0;
}

export const FUNCS: Record<string, (args: Arg[]) => Arg> = {
  SUM: agg((ns) => ns.reduce((a, b) => a + b, 0)),
  PRODUCT: agg((ns) => ns.reduce((a, b) => a * b, 1)),
  AVERAGE: agg((ns) => ns.reduce((a, b) => a + b, 0) / ns.length, ERR.DIV0),
  MEDIAN: agg((ns) => {
    const s = [...ns].sort((a, b) => a - b);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }, ERR.NUM),
  MIN: agg((ns) => Math.min(...ns)),
  MAX: agg((ns) => Math.max(...ns)),
  COUNT: (args) => {
    const ns = nums(args);
    return Array.isArray(ns) ? ns.length : ns;
  },
  COUNTA: (args) => flat(args).filter((v) => v != null && v !== '').length,
  COUNTBLANK: (args) => flat(args).filter((v) => v == null || v === '').length,
  STDEV: agg((ns) => {
    if (ns.length < 2) return NaN;
    const m = ns.reduce((a, b) => a + b, 0) / ns.length;
    return Math.sqrt(ns.reduce((a, b) => a + (b - m) * (b - m), 0) / (ns.length - 1));
  }, ERR.DIV0),
  VAR: agg((ns) => {
    if (ns.length < 2) return NaN;
    const m = ns.reduce((a, b) => a + b, 0) / ns.length;
    return ns.reduce((a, b) => a + (b - m) * (b - m), 0) / (ns.length - 1);
  }, ERR.DIV0),
  ABS: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.abs(n) : n; },
  SQRT: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? (n < 0 ? ERR.NUM : Math.sqrt(n)) : n; },
  POWER: (a) => {
    const x = toNum(a[0]); const y = toNum(a[1]);
    if (typeof x !== 'number') return x;
    if (typeof y !== 'number') return y;
    return Math.pow(x, y);
  },
  MOD: (a) => {
    const x = toNum(a[0]); const y = toNum(a[1]);
    if (typeof x !== 'number') return x;
    if (typeof y !== 'number') return y;
    if (y === 0) return ERR.DIV0;
    return x - Math.floor(x / y) * y;
  },
  INT: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.floor(n) : n; },
  ROUND: (a) => roundTo(a, (n, m) => Math.round(n * m) / m),
  ROUNDUP: (a) => roundTo(a, (n, m) => Math.sign(n) * Math.ceil(Math.abs(n) * m) / m),
  ROUNDDOWN: (a) => roundTo(a, (n, m) => Math.sign(n) * Math.floor(Math.abs(n) * m) / m),
  CEILING: (a) => {
    const n = toNum(a[0]); const s = a.length > 1 ? toNum(a[1]) : 1;
    if (typeof n !== 'number') return n;
    if (typeof s !== 'number') return s;
    if (s === 0) return 0;
    return Math.ceil(n / s) * s;
  },
  FLOOR: (a) => {
    const n = toNum(a[0]); const s = a.length > 1 ? toNum(a[1]) : 1;
    if (typeof n !== 'number') return n;
    if (typeof s !== 'number') return s;
    if (s === 0) return 0;
    return Math.floor(n / s) * s;
  },
  EXP: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.exp(n) : n; },
  LN: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? (n <= 0 ? ERR.NUM : Math.log(n)) : n; },
  LOG: (a) => {
    const n = toNum(a[0]); const b = a.length > 1 ? toNum(a[1]) : 10;
    if (typeof n !== 'number') return n;
    if (typeof b !== 'number') return b;
    if (n <= 0 || b <= 0) return ERR.NUM;
    return Math.log(n) / Math.log(b);
  },
  LOG10: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? (n <= 0 ? ERR.NUM : Math.log10(n)) : n; },
  PI: () => Math.PI,
  RAND: () => Math.random(),
  RANDBETWEEN: (a) => {
    const lo = toNum(a[0]); const hi = toNum(a[1]);
    if (typeof lo !== 'number') return lo;
    if (typeof hi !== 'number') return hi;
    return Math.floor(Math.random() * (hi - lo + 1)) + lo;
  },
  SIN: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.sin(n) : n; },
  COS: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.cos(n) : n; },
  TAN: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.tan(n) : n; },

  // IF/IFS/IFERROR/IFNA/ROW/COLUMN are evaluated lazily in engine.evalCall.
  AND: (a) => {
    for (const v of flat(a)) {
      if (isErr(v)) return v;
      if (v == null || v === '') continue;
      const b = toBool(v);
      if (typeof b !== 'boolean') return b;
      if (!b) return false;
    }
    return true;
  },
  OR: (a) => {
    for (const v of flat(a)) {
      if (isErr(v)) return v;
      if (v == null || v === '') continue;
      const b = toBool(v);
      if (typeof b !== 'boolean') return b;
      if (b) return true;
    }
    return false;
  },
  NOT: (a) => { const b = toBool(a[0]); return typeof b === 'boolean' ? !b : b; },
  XOR: (a) => {
    let n = 0;
    for (const v of flat(a)) {
      if (isErr(v)) return v;
      const b = toBool(v);
      if (typeof b !== 'boolean') return b;
      if (b) n++;
    }
    return n % 2 === 1;
  },
  ISBLANK: (a) => { const v = Array.isArray(a[0]) ? a[0][0]?.[0] : a[0]; return v == null || v === ''; },
  ISNUMBER: (a) => typeof (Array.isArray(a[0]) ? a[0][0]?.[0] : a[0]) === 'number',
  ISTEXT: (a) => typeof (Array.isArray(a[0]) ? a[0][0]?.[0] : a[0]) === 'string',
  ISERROR: (a) => isErr(Array.isArray(a[0]) ? a[0][0]?.[0] : a[0]),
  ISLOGICAL: (a) => typeof (Array.isArray(a[0]) ? a[0][0]?.[0] : a[0]) === 'boolean',
  NA: () => ERR.NA,

  CONCAT: (a) => concatAll(a),
  CONCATENATE: (a) => concatAll(a),
  TEXTJOIN: (a) => {
    const sep = toStr(a[0]);
    if (typeof sep !== 'string') return sep;
    const ignoreEmpty = a.length > 1 ? toBool(a[1]) : true;
    if (typeof ignoreEmpty !== 'boolean') return ignoreEmpty;
    const vals = flat(a.slice(2));
    const e = firstErr(vals);
    if (e) return e;
    const parts = vals.map((v) => (v == null ? '' : typeof v === 'boolean' ? (v ? 'TRUE' : 'FALSE') : String(v)));
    return (ignoreEmpty ? parts.filter((s) => s !== '') : parts).join(sep);
  },
  LEFT: (a) => {
    const s = toStr(a[0]); const n = a.length > 1 ? toNum(a[1]) : 1;
    if (typeof s !== 'string') return s;
    if (typeof n !== 'number') return n;
    return s.slice(0, Math.max(0, n));
  },
  RIGHT: (a) => {
    const s = toStr(a[0]); const n = a.length > 1 ? toNum(a[1]) : 1;
    if (typeof s !== 'string') return s;
    if (typeof n !== 'number') return n;
    return n <= 0 ? '' : s.slice(-n);
  },
  MID: (a) => {
    const s = toStr(a[0]); const st = toNum(a[1]); const n = toNum(a[2]);
    if (typeof s !== 'string') return s;
    if (typeof st !== 'number') return st;
    if (typeof n !== 'number') return n;
    if (st < 1 || n < 0) return ERR.VALUE;
    return s.slice(st - 1, st - 1 + n);
  },
  LEN: (a) => { const s = toStr(a[0]); return typeof s === 'string' ? s.length : s; },
  LOWER: (a) => { const s = toStr(a[0]); return typeof s === 'string' ? s.toLowerCase() : s; },
  UPPER: (a) => { const s = toStr(a[0]); return typeof s === 'string' ? s.toUpperCase() : s; },
  PROPER: (a) => {
    const s = toStr(a[0]);
    return typeof s === 'string' ? s.replace(/\w+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase()) : s;
  },
  TRIM: (a) => { const s = toStr(a[0]); return typeof s === 'string' ? s.trim().replace(/ +/g, ' ') : s; },
  SUBSTITUTE: (a) => {
    const s = toStr(a[0]); const oldS = toStr(a[1]); const newS = toStr(a[2]);
    if (typeof s !== 'string') return s;
    if (typeof oldS !== 'string') return oldS;
    if (typeof newS !== 'string') return newS;
    if (oldS === '') return s;
    if (a.length > 3) {
      const n = toNum(a[3]);
      if (typeof n !== 'number') return n;
      let idx = -1;
      for (let i = 0; i < n; i++) {
        idx = s.indexOf(oldS, idx + 1);
        if (idx === -1) return s;
      }
      return s.slice(0, idx) + newS + s.slice(idx + oldS.length);
    }
    return s.split(oldS).join(newS);
  },
  REPLACE: (a) => {
    const s = toStr(a[0]); const st = toNum(a[1]); const n = toNum(a[2]); const rep = toStr(a[3]);
    if (typeof s !== 'string') return s;
    if (typeof st !== 'number') return st;
    if (typeof n !== 'number') return n;
    if (typeof rep !== 'string') return rep;
    return s.slice(0, st - 1) + rep + s.slice(st - 1 + n);
  },
  REPT: (a) => {
    const s = toStr(a[0]); const n = toNum(a[1]);
    if (typeof s !== 'string') return s;
    if (typeof n !== 'number') return n;
    if (n < 0 || s.length * n > 32767) return ERR.VALUE;
    return s.repeat(Math.floor(n));
  },
  FIND: (a) => {
    const what = toStr(a[0]); const where = toStr(a[1]); const st = a.length > 2 ? toNum(a[2]) : 1;
    if (typeof what !== 'string') return what;
    if (typeof where !== 'string') return where;
    if (typeof st !== 'number') return st;
    const idx = where.indexOf(what, st - 1);
    return idx === -1 ? ERR.VALUE : idx + 1;
  },
  SEARCH: (a) => {
    const what = toStr(a[0]); const where = toStr(a[1]); const st = a.length > 2 ? toNum(a[2]) : 1;
    if (typeof what !== 'string') return what;
    if (typeof where !== 'string') return where;
    if (typeof st !== 'number') return st;
    const idx = where.toLowerCase().indexOf(what.toLowerCase(), st - 1);
    return idx === -1 ? ERR.VALUE : idx + 1;
  },
  EXACT: (a) => {
    const x = toStr(a[0]); const y = toStr(a[1]);
    if (typeof x !== 'string') return x;
    if (typeof y !== 'string') return y;
    return x === y;
  },
  VALUE: (a) => {
    const s = toStr(a[0]);
    if (typeof s !== 'string') return s;
    const n = Number(s.replace(/[$,%\s]/g, ''));
    if (!Number.isFinite(n)) return ERR.VALUE;
    return /%\s*$/.test(s) ? n / 100 : n;
  },
  TEXT: (a) => {
    const n = toNum(a[0]); const fmt = toStr(a[1]);
    if (typeof n !== 'number') return typeof a[0] === 'string' ? a[0] : n;
    if (typeof fmt !== 'string') return fmt;
    return formatNumber(n, fmt);
  },
  CHAR: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? String.fromCharCode(n) : n; },
  CODE: (a) => { const s = toStr(a[0]); return typeof s === 'string' ? (s ? s.charCodeAt(0) : ERR.VALUE) : s; },

  SUMIF: (a) => {
    const pairs = condPairs([a[0], a[1]]);
    if (!Array.isArray(pairs)) return pairs;
    const valGrid = a.length > 2 ? asGrid(a[2]) : null;
    return multiCond(valGrid, pairs, sumVals);
  },
  SUMIFS: (a) => {
    const pairs = condPairs(a.slice(1));
    if (!Array.isArray(pairs)) return pairs;
    return multiCond(asGrid(a[0]), pairs, sumVals);
  },
  COUNTIF: (a) => {
    const pairs = condPairs([a[0], a[1]]);
    if (!Array.isArray(pairs)) return pairs;
    return multiCond(null, pairs, (vals) => vals.length);
  },
  COUNTIFS: (a) => {
    const pairs = condPairs(a);
    if (!Array.isArray(pairs)) return pairs;
    return multiCond(null, pairs, (vals) => vals.length);
  },
  AVERAGEIF: (a) => {
    const pairs = condPairs([a[0], a[1]]);
    if (!Array.isArray(pairs)) return pairs;
    const valGrid = a.length > 2 ? asGrid(a[2]) : null;
    return multiCond(valGrid, pairs, avgVals);
  },
  AVERAGEIFS: (a) => {
    const pairs = condPairs(a.slice(1));
    if (!Array.isArray(pairs)) return pairs;
    return multiCond(asGrid(a[0]), pairs, avgVals);
  },
  SUMPRODUCT: (a) => {
    const grids = a.map(asGrid);
    const rows = grids[0].length;
    const cols = grids[0][0]?.length || 0;
    let total = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        let prod = 1;
        for (const g of grids) {
          const v = g[r]?.[c];
          if (isErr(v)) return v;
          prod *= typeof v === 'number' ? v : typeof v === 'boolean' ? (v ? 1 : 0) : 0;
        }
        total += prod;
      }
    }
    return total;
  },

  VLOOKUP: (a) => {
    const needle = Array.isArray(a[0]) ? a[0][0]?.[0] ?? null : a[0];
    if (isErr(needle)) return needle;
    const table = asGrid(a[1]);
    const colN = toNum(a[2]);
    if (typeof colN !== 'number') return colN;
    // Excel: omitted range_lookup defaults to TRUE (approximate match).
    const rangeLookup = a.length > 3 ? toBool(a[3]) : true;
    if (typeof rangeLookup !== 'boolean') return rangeLookup;
    const approx = rangeLookup;
    const ci = Math.floor(colN) - 1;
    if (ci < 0 || ci >= (table[0]?.length || 0)) return ERR.REF;
    if (!approx) {
      for (const row of table) {
        if (eqLookup(row[0], needle)) return row[ci] ?? null;
      }
      return ERR.NA;
    }
    let best: EvalValue[] | null = null;
    for (const row of table) {
      const k = row[0];
      if (k == null || k === '') continue;
      if (cmpVals(k, needle) <= 0) best = row;
      else break;
    }
    return best ? best[ci] ?? null : ERR.NA;
  },
  HLOOKUP: (a) => {
    const needle = Array.isArray(a[0]) ? a[0][0]?.[0] ?? null : a[0];
    if (isErr(needle)) return needle;
    const table = asGrid(a[1]);
    const rowN = toNum(a[2]);
    if (typeof rowN !== 'number') return rowN;
    const rangeLookup = a.length > 3 ? toBool(a[3]) : true;
    if (typeof rangeLookup !== 'boolean') return rangeLookup;
    const approx = rangeLookup;
    const ri = Math.floor(rowN) - 1;
    if (ri < 0 || ri >= table.length) return ERR.REF;
    const header = table[0] || [];
    if (!approx) {
      for (let c = 0; c < header.length; c++) {
        if (eqLookup(header[c], needle)) return table[ri]?.[c] ?? null;
      }
      return ERR.NA;
    }
    let bestC = -1;
    for (let c = 0; c < header.length; c++) {
      const k = header[c];
      if (k == null || k === '') continue;
      if (cmpVals(k, needle) <= 0) bestC = c;
      else break;
    }
    return bestC === -1 ? ERR.NA : table[ri]?.[bestC] ?? null;
  },
  XLOOKUP: (a) => {
    const needle = Array.isArray(a[0]) ? a[0][0]?.[0] ?? null : a[0];
    if (isErr(needle)) return needle;
    const lookup = asGrid(a[1]);
    const ret = asGrid(a[2]);
    const notFound = a.length > 3 ? a[3] : ERR.NA;
    const flatL = lookup.flat();
    const vertical = lookup.length >= (lookup[0]?.length || 1);
    for (let i = 0; i < flatL.length; i++) {
      if (eqLookup(flatL[i], needle)) {
        return vertical ? (ret[i]?.length === 1 ? ret[i][0] : (ret[i] ? [ret[i]] : ERR.NA) as any) : ret.map((row) => [row[i]]) as any;
      }
    }
    return notFound;
  },
  INDEX: (a) => {
    const grid = asGrid(a[0]);
    const rn = toNum(a[1]);
    if (typeof rn !== 'number') return rn;
    const cn = a.length > 2 ? toNum(a[2]) : (grid[0]?.length === 1 || grid.length === 1 ? 0 : 1);
    if (typeof cn !== 'number') return cn;
    if (grid.length === 1 && a.length < 3) return grid[0][Math.floor(rn) - 1] ?? ERR.REF;
    const row = grid[Math.floor(rn) - 1];
    if (!row) return ERR.REF;
    if (cn === 0) return grid[0].length === 1 ? row[0] : ([row] as any);
    const v = row[Math.floor(cn) - 1];
    return v === undefined ? ERR.REF : v;
  },
  MATCH: (a) => {
    const needle = Array.isArray(a[0]) ? a[0][0]?.[0] ?? null : a[0];
    if (isErr(needle)) return needle;
    const grid = asGrid(a[1]);
    const mode = a.length > 2 ? toNum(a[2]) : 1;
    if (typeof mode !== 'number') return mode;
    const vals = grid.length === 1 ? grid[0] : grid.map((r) => r[0]);
    if (mode === 0) {
      for (let i = 0; i < vals.length; i++) if (eqLookup(vals[i], needle)) return i + 1;
      return ERR.NA;
    }
    let best = -1;
    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (v == null || v === '') continue;
      const cmp = cmpVals(v, needle);
      if (mode > 0) { if (cmp <= 0) best = i; else break; }
      else { if (cmp >= 0) best = i; else break; }
    }
    return best === -1 ? ERR.NA : best + 1;
  },
  CHOOSE: (a) => {
    const n = toNum(a[0]);
    if (typeof n !== 'number') return n;
    const idx = Math.floor(n);
    if (idx < 1 || idx >= a.length) return ERR.VALUE;
    return a[idx];
  },
  TRANSPOSE: (a) => {
    const g = asGrid(a[0]);
    const out: EvalValue[][] = [];
    for (let c = 0; c < (g[0]?.length || 0); c++) out.push(g.map((row) => row[c] ?? null));
    return out as any;
  },
  UNIQUE: (a) => {
    const g = asGrid(a[0]);
    const seen = new Set<string>();
    const out: EvalValue[][] = [];
    for (const row of g) {
      const k = JSON.stringify(row);
      if (!seen.has(k)) { seen.add(k); out.push(row); }
    }
    return out.length ? (out as any) : ERR.NA;
  },
  SORT: (a) => {
    const g = asGrid(a[0]).map((r) => [...r]);
    const colN = a.length > 1 ? toNum(a[1]) : 1;
    if (typeof colN !== 'number') return colN;
    const dir = a.length > 2 ? toNum(a[2]) : 1;
    if (typeof dir !== 'number') return dir;
    const ci = Math.floor(colN) - 1;
    g.sort((x, y) => cmpVals(x[ci] ?? null, y[ci] ?? null) * (dir < 0 ? -1 : 1));
    return g as any;
  },
  FILTER: (a) => {
    const g = asGrid(a[0]);
    const cond = asGrid(a[1]);
    const out: EvalValue[][] = [];
    for (let r = 0; r < g.length; r++) {
      const cv = cond[r]?.[0];
      const b = toBool(cv ?? null);
      if (typeof b !== 'boolean') return b;
      if (b) out.push(g[r]);
    }
    if (!out.length) return a.length > 2 ? a[2] : ERR.NA;
    return out as any;
  },
  TODAY: () => dateToSerial(new Date()),
  NOW: () => {
    const d = new Date();
    return dateToSerial(d) + (d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds()) / 86400;
  },
  DATE: (a) => {
    const y = toNum(a[0]); const m = toNum(a[1]); const d = toNum(a[2]);
    if (typeof y !== 'number') return y;
    if (typeof m !== 'number') return m;
    if (typeof d !== 'number') return d;
    return (Date.UTC(y, m - 1, d) - EPOCH) / DAY_MS;
  },
  YEAR: (a) => dateField(a[0], (d) => d.getUTCFullYear()),
  MONTH: (a) => dateField(a[0], (d) => d.getUTCMonth() + 1),
  DAY: (a) => dateField(a[0], (d) => d.getUTCDate()),
  WEEKDAY: (a) => dateField(a[0], (d) => d.getUTCDay() + 1),
  HOUR: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.floor((n % 1) * 24) : n; },
  MINUTE: (a) => { const n = toNum(a[0]); return typeof n === 'number' ? Math.floor((n * 1440) % 60) : n; },
  DATEDIF: (a) => {
    const s = toNum(a[0]); const e = toNum(a[1]); const u = toStr(a[2]);
    if (typeof s !== 'number') return s;
    if (typeof e !== 'number') return e;
    if (typeof u !== 'string') return u;
    if (e < s) return ERR.NUM;
    const sd = serialToDate(s); const ed = serialToDate(e);
    switch (u.toUpperCase()) {
      case 'D': return Math.floor(e - s);
      case 'M': return (ed.getUTCFullYear() - sd.getUTCFullYear()) * 12 + ed.getUTCMonth() - sd.getUTCMonth() - (ed.getUTCDate() < sd.getUTCDate() ? 1 : 0);
      case 'Y': {
        let y = ed.getUTCFullYear() - sd.getUTCFullYear();
        if (ed.getUTCMonth() < sd.getUTCMonth() || (ed.getUTCMonth() === sd.getUTCMonth() && ed.getUTCDate() < sd.getUTCDate())) y--;
        return y;
      }
      default: return ERR.VALUE;
    }
  },
};

function sumVals(vals: EvalValue[]): Arg {
  let total = 0;
  for (const v of vals) {
    if (isErr(v)) return v;
    if (typeof v === 'number') total += v;
  }
  return total;
}

function avgVals(vals: EvalValue[]): Arg {
  let total = 0;
  let n = 0;
  for (const v of vals) {
    if (isErr(v)) return v;
    if (typeof v === 'number') { total += v; n++; }
  }
  return n ? total / n : ERR.DIV0;
}

function eqLookup(a: EvalValue | undefined, b: EvalValue): boolean {
  if (a == null) return b == null || b === '';
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (typeof a === 'string' && typeof b === 'string') return a.toLowerCase() === b.toLowerCase();
  if (typeof a === 'number' && typeof b === 'string') return String(a) === b.trim();
  if (typeof a === 'string' && typeof b === 'number') return a.trim() === String(b);
  return a === b;
}

function roundTo(a: Arg[], fn: (n: number, mult: number) => number): Arg {
  const n = toNum(a[0]);
  const digits = a.length > 1 ? toNum(a[1]) : 0;
  if (typeof n !== 'number') return n;
  if (typeof digits !== 'number') return digits;
  const m = Math.pow(10, Math.floor(digits));
  const out = fn(n, m);
  return Object.is(out, -0) ? 0 : out;
}

function concatAll(a: Arg[]): Arg {
  const vals = flat(a);
  const e = firstErr(vals);
  if (e) return e;
  return vals.map((v) => (v == null ? '' : typeof v === 'boolean' ? (v ? 'TRUE' : 'FALSE') : String(v))).join('');
}

function dateField(a: Arg, fn: (d: Date) => number): Arg {
  const n = toNum(a);
  if (typeof n !== 'number') return n;
  return fn(serialToDate(n));
}

export function formatNumber(n: number, fmt: string): string {
  if (/[ymdhs]/i.test(fmt) && !/[0#]/.test(fmt)) {
    const d = serialToDate(n);
    const pad = (x: number) => String(x).padStart(2, '0');
    return fmt
      .replace(/yyyy/gi, String(d.getUTCFullYear()))
      .replace(/yy/gi, String(d.getUTCFullYear()).slice(-2))
      .replace(/mm/gi, pad(d.getUTCMonth() + 1))
      .replace(/dd/gi, pad(d.getUTCDate()))
      .replace(/m/gi, String(d.getUTCMonth() + 1))
      .replace(/d/gi, String(d.getUTCDate()));
  }
  const pct = fmt.includes('%');
  let x = pct ? n * 100 : n;
  const decM = /\.([0#]+)/.exec(fmt);
  const decimals = decM ? decM[1].length : 0;
  const grouped = fmt.includes(',');
  let s = x.toFixed(decimals);
  if (grouped) {
    const [ip, dp] = s.split('.');
    const neg = ip.startsWith('-');
    const digits = neg ? ip.slice(1) : ip;
    const withCommas = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    s = (neg ? '-' : '') + withCommas + (dp ? '.' + dp : '');
  }
  if (fmt.includes('$')) s = (x < 0 ? '-$' : '$') + s.replace(/^-/, '');
  return s + (pct ? '%' : '');
}
